/**
 * Phase 86 Plan 86-05 Task 3 — BULK CLOSE chip + confirmation dialog.
 *
 * Mounts into `MtZoneBand`'s graveyard-only `trailing` slot (the 86-04 slot
 * contract — see `MyTasksZonedList.tsx`'s `graveyardTrailingSlot` prop).
 * Self-contained: owns both the trigger chip (styled to the handoff's
 * graveyard-band chip spec — README "Zone header bands" table, GRAVEYARD
 * row) and the confirmation dialog, the same way `MyTaskRow` owns its own
 * popover trigger + panel.
 *
 * Renders nothing when there is nothing to close (`tasks.length === 0`) —
 * the graveyard band itself already only mounts when non-empty (86-04's
 * `groupTasksByZone` skips empty zones), but this component also guards
 * itself so it is safe to render unconditionally.
 *
 * This is a CONFIRMATION, not a one-click action (README "BULK CLOSE...
 * selects the zone and asks for confirmation with the count"): the
 * confirming button's own label states the count so the number is
 * unavoidable, and the body lists every affected project so a cross-project
 * close is never a surprise. Dispatches through `useCloseTasks` (Task 2) and
 * handles partial failure honestly — successes leave the list via the
 * hook's own invalidation, failures stay in the dialog named by project
 * (T-86-16 / T-86-18).
 *
 * Reuses the existing `Modal` (`components/common/Modal.tsx`) rather than a
 * bespoke dialog shell — it already traps focus, restores focus to the
 * trigger on close, and dismisses on Escape.
 */
import { useState } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { useCloseTasks, type CloseTaskFailure } from '../../hooks/useCloseTasks';
import { GRAVEYARD_BORDER_HEX, GRAVEYARD_DARK_HEX } from './compostTokens';

export interface MtBulkCloseTarget {
  taskId: number;
  projectId: number;
  projectName: string;
}

export interface MtBulkCloseDialogProps {
  /** The graveyard zone's current tasks. Empty ⇒ renders nothing. */
  tasks: MtBulkCloseTarget[];
}

export function MtBulkCloseDialog({ tasks }: MtBulkCloseDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [failures, setFailures] = useState<CloseTaskFailure[]>([]);
  const closeTasks = useCloseTasks();

  if (tasks.length === 0) return null;

  const count = tasks.length;
  const projectNames = Array.from(new Set(tasks.map((t) => t.projectName))).sort((a, b) =>
    a.localeCompare(b),
  );

  function handleCancel() {
    setIsOpen(false);
    setFailures([]);
  }

  function handleConfirm() {
    closeTasks.mutate(tasks, {
      onSuccess: (result) => {
        setFailures(result.failures);
        if (result.failures.length === 0) {
          setIsOpen(false);
        }
        // Partial (or total) failure: keep the dialog open, named failures
        // render below — the successes have already left the list via the
        // hook's own my-tasks invalidation.
      },
    });
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        data-testid="mt-bulk-close-chip"
        aria-label={`Bulk close ${count} graveyard task${count === 1 ? '' : 's'}`}
        className="shrink-0 inline-flex items-center px-2.5 py-1 rounded font-mono text-[9.5px] font-bold border bg-white"
        style={{ borderColor: GRAVEYARD_BORDER_HEX, color: GRAVEYARD_DARK_HEX }}
      >
        BULK CLOSE
      </button>
      <Modal
        isOpen={isOpen}
        onClose={handleCancel}
        title="Close all graveyard tasks?"
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={handleCancel} disabled={closeTasks.isPending}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleConfirm} isLoading={closeTasks.isPending}>
              {`Close ${count} task${count === 1 ? '' : 's'}`}
            </Button>
          </div>
        }
      >
        <p className="text-sm text-gray-700">
          This closes {count} task{count === 1 ? '' : 's'} across {projectNames.length} project
          {projectNames.length === 1 ? '' : 's'}:
        </p>
        <ul className="mt-2 text-sm text-gray-700 list-disc list-inside" data-testid="mt-bulk-close-projects">
          {projectNames.map((name) => (
            <li key={name}>{name}</li>
          ))}
        </ul>
        {failures.length > 0 && (
          <div className="mt-4 text-sm text-rose-700" data-testid="mt-bulk-close-failures">
            <p className="font-semibold">Couldn&apos;t close:</p>
            <ul className="list-disc list-inside">
              {failures.map((f) => (
                <li key={f.taskId}>
                  {f.projectName} — {f.reason}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Modal>
    </>
  );
}
